import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { FileInput, FileField, ImageField, useTranslate } from 'react-admin';
import { Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import ImportIcon from '@/assets/icons/import-icon.svg?react';
import ImportLoadingIcon from '@/assets/icons/loading-icon.svg?react';
import { styled } from '@mui/material/styles';
import { importInputBaseStyles } from '@/themes/commonStyles';
import { useWatch } from 'react-hook-form';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledImportImageInput = styled(Box)({
    ...importInputBaseStyles,
    '& .RaFileInput-dropZone': {
        ...importInputBaseStyles['& .RaFileInput-dropZone'],
        position: 'relative',
        '& .import-info': {
            ...importInputBaseStyles['& .RaFileInput-dropZone']['& .import-info'],
            '& .import-preview': {
                display: 'block',
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '12px',
                margin: '0 auto',
            },
            '& .import-preview-ra img': {
                display: 'block',
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '12px',
                margin: '0 auto',
            },
            '& .btn-import': {
                ...importInputBaseStyles['& .RaFileInput-dropZone']['& .import-info']['& .btn-import'],
                '&:hover': {
                    backgroundColor: Palette.yellow.yellow400,
                    boxShadow: `inset 0 0 0 2px ${Palette.yellow.yellow600}`,
                },
            },
        },
    },
    '&.has-preview': {
        '& .previews': {
            display: 'none',
        },
        '& .RaFileInput-dropZone .import-info': {
            margin: '16px',
        },
        '& .RaImageField-image': {
            width: '100%',
        },
    },
});

const ImportImageInput = ({ source = 'image', accept = '.jpg,.jpeg,.png', validate, placeholderText, loading = false, disabled = false }) => {
    const translate = useTranslate();
    const placeholder = loading ? translate('resources.campaign.labels.verifyingFile') : translate('resources.campaign.labels.chooseFile');
    const value = useWatch({ name: source });

    const containerRef = useRef(null);
    const handleReupload = useCallback(
        event => {
            event.preventDefault();
            event.stopPropagation();
            if (event.nativeEvent && typeof event.nativeEvent.stopImmediatePropagation === 'function') {
                event.nativeEvent.stopImmediatePropagation();
            }
            const input = containerRef.current?.querySelector('input[type="file"]');
            if (input && !disabled) {
                input.click();
            }
        },
        [disabled],
    );

    const previewUrl = useMemo(() => {
        if (!value) return null;
        const first = Array.isArray(value) ? value[0] : value;
        if (!first) return null;
        const raw = first.rawFile || first;
        if (!(raw instanceof File)) return null;
        try {
            return URL.createObjectURL(raw);
        } catch {
            return null;
        }
    }, [value]);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    return (
        <StyledImportImageInput ref={containerRef} className={previewUrl ? 'has-preview' : ''}>
            <FileInput
                source={source}
                accept={accept}
                validate={validate}
                disabled={disabled}
                placeholder={
                    <Typography className='import-info'>
                        {!previewUrl && (
                            <>
                                <Typography variant='body1'>{placeholderText}</Typography>
                                <Typography variant='body1' className={`btn-import ${loading ? 'loading' : 'loaded'}`} disabled={loading}>
                                    {loading && <ImportLoadingIcon />}
                                    {!loading && <ImportIcon />}
                                    {placeholder}
                                </Typography>
                            </>
                        )}
                        {previewUrl && (
                            <>
                                <ImageField record={{ src: previewUrl }} source='src' className='import-preview-ra' />
                                <Typography
                                    variant='body1'
                                    className='btn-import loaded'
                                    role='button'
                                    tabIndex={0}
                                    onClick={handleReupload}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleReupload(e);
                                        }
                                    }}
                                >
                                    <ImportIcon />
                                    {translate('resources.campaign.labels.reupload')}
                                </Typography>
                            </>
                        )}
                    </Typography>
                }
            >
                <FileField source='src' disabled={disabled} />
            </FileInput>
        </StyledImportImageInput>
    );
};

ImportImageInput.propTypes = {
    source: PropTypes.string,
    accept: PropTypes.string,
    validate: PropTypes.func,
    placeholderText: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default ImportImageInput;
